/**
 * ComponentPalette — Left panel of the Visual Workspace Designer.
 *
 * Contains collapsible sections:
 * - Reusable Components (fetched from /api/v1/ui-components — views, pickers, panels)
 * - Layout (Tab, Section, Columns)
 * - Panels (Grid, Detail Grid, Form, KPI, Chart)
 * - Field Types (Text, Number, Date, Checkbox, Dropdown, etc.)
 * - Actions (Button, Link, etc.)
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Collapse,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TabIcon from '@mui/icons-material/Tab';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import TableChartIcon from '@mui/icons-material/TableChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EditNoteIcon from '@mui/icons-material/EditNote';
import BarChartIcon from '@mui/icons-material/BarChart';
import SpeedIcon from '@mui/icons-material/Speed';
import ExtensionIcon from '@mui/icons-material/Extension';
import SearchIcon from '@mui/icons-material/Search';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import WidgetsIcon from '@mui/icons-material/Widgets';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { useDraggable } from '@dnd-kit/core';

// ---------------------------------------------------------------------------
// Draggable Item wrapper
// ---------------------------------------------------------------------------
function DraggableItem({ id, data, children }) {
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id,
    data,
  });

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      role="button"
      tabIndex={0}
      sx={{
        opacity: isDragging ? 0.4 : 1,
        cursor: 'grab',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Palette item chip
// ---------------------------------------------------------------------------
function PaletteChip({ icon, label, id, dragData, colors }) {
  return (
    <DraggableItem id={id} data={dragData}>
      <Chip
        icon={icon}
        label={label}
        size="small"
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          bgcolor: 'rgba(255,255,255,0.05)',
          color: colors?.text || '#e0e0e0',
          border: '1px solid rgba(255,255,255,0.08)',
          cursor: 'grab',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          '& .MuiChip-icon': { color: colors?.textSecondary || '#999', fontSize: 16 },
          '& .MuiChip-label': { fontSize: '0.75rem' },
          height: 32,
        }}
      />
    </DraggableItem>
  );
}

// ---------------------------------------------------------------------------
// Collapsible section
// ---------------------------------------------------------------------------
function PaletteSection({ title, defaultOpen = true, children, colors }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
        }}
      >
        <Typography
          variant="overline"
          sx={{ flex: 1, color: colors?.textSecondary || '#888', fontSize: '0.65rem', letterSpacing: 1 }}
        >
          {title}
        </Typography>
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 16, color: colors?.textSecondary || '#888' }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 16, color: colors?.textSecondary || '#888' }} />
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Field type definitions (drag these onto panels, then configure entity/field in properties)
// ---------------------------------------------------------------------------
const FIELD_TYPES = [
  { fieldType: 'TEXT',       label: 'Text Field',     icon: 'Ab' },
  { fieldType: 'NUMBER',     label: 'Number',         icon: '#'  },
  { fieldType: 'BOOLEAN',    label: 'Checkbox',       icon: '☐' },
  { fieldType: 'DATE',       label: 'Date',           icon: '📅' },
  { fieldType: 'DATETIME',   label: 'Date & Time',    icon: '🕐' },
  { fieldType: 'DROPDOWN',   label: 'Dropdown',       icon: '▼' },
  { fieldType: 'COMBOBOX',   label: 'Combobox',       icon: '▼' },
  { fieldType: 'REFERENCE',  label: 'Lookup / Ref',   icon: '🔗' },
  { fieldType: 'STATUS',     label: 'Status',         icon: '◉' },
  { fieldType: 'TEXTAREA',   label: 'Text Area',      icon: '¶' },
  { fieldType: 'EMAIL',      label: 'Email',          icon: '@'  },
  { fieldType: 'PASSWORD',   label: 'Password',       icon: '••' },
];

// ---------------------------------------------------------------------------
// Action type definitions
// ---------------------------------------------------------------------------
const ACTION_TYPES = [
  { actionType: 'button',  label: 'Button',       icon: '▶' },
  { actionType: 'link',    label: 'Link',         icon: '🔗' },
  { actionType: 'submit',  label: 'Submit',       icon: '✓' },
  { actionType: 'delete',  label: 'Delete',       icon: '✕' },
];

// ---------------------------------------------------------------------------
// Component type icons for reusable components
// ---------------------------------------------------------------------------
const COMPONENT_TYPE_ICONS = {
  VIEW: ViewQuiltIcon,
  PANEL: WidgetsIcon,
  PICKER: TouchAppIcon,
  FORM: EditNoteIcon,
  WIZARD: DashboardCustomizeIcon,
  WORKSPACE_TEMPLATE: TabIcon,
};

const COMPONENT_TYPE_LABELS = {
  VIEW: 'Views',
  PANEL: 'Panels',
  PICKER: 'Pickers',
  FORM: 'Forms',
  WIZARD: 'Wizards',
  WORKSPACE_TEMPLATE: 'Templates',
};

// Ordered list of component types to display
const COMPONENT_TYPE_ORDER = ['VIEW', 'PANEL', 'PICKER', 'FORM', 'WIZARD', 'WORKSPACE_TEMPLATE'];

// ---------------------------------------------------------------------------
// Hook — fetch reusable UI components from API
// ---------------------------------------------------------------------------
function useUiComponents() {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('waide_access_token');
    const headers = { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) };
    fetch('/api/v1/ui-components', { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setComponents(Array.isArray(data) ? data : []))
      .catch(() => setComponents([]))
      .finally(() => setLoading(false));
  }, []);

  return { components, loading };
}

// ---------------------------------------------------------------------------
// ComponentPalette
// ---------------------------------------------------------------------------
export default function ComponentPalette({ colors }) {
  const { components, loading } = useUiComponents();
  const [searchTerm, setSearchTerm] = useState('');

  // Group components by type, filtered by search
  const filteredComponents = searchTerm
    ? components.filter((c) =>
        (c.displayName || c.componentCode || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.componentType || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : components;

  const groupedComponents = COMPONENT_TYPE_ORDER.reduce((acc, type) => {
    const group = filteredComponents.filter((c) => c.componentType === type);
    if (group.length > 0) acc[type] = group;
    return acc;
  }, {});

  return (
    <Box
      sx={{
        width: 240,
        minWidth: 240,
        borderRight: `1px solid ${colors?.border || '#333'}`,
        bgcolor: colors?.surface || '#1a1a2e',
        overflow: 'auto',
        flexShrink: 0,
      }}
    >
      <Box sx={{ py: 1 }}>
        {/* Reusable Components Section */}
        <PaletteSection title="Reusable Components" defaultOpen={true} colors={colors}>
          {/* Search bar */}
          <TextField
            size="small"
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14, color: colors?.textSecondary || '#666' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 0.5,
              '& .MuiOutlinedInput-root': {
                color: colors?.text || '#e0e0e0',
                fontSize: '0.7rem',
                height: 28,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&.Mui-focused fieldset': { borderColor: '#22c55e' },
              },
              '& .MuiInputBase-input': { py: '2px' },
            }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={16} sx={{ color: colors?.textSecondary || '#666' }} />
            </Box>
          ) : Object.keys(groupedComponents).length === 0 ? (
            <Typography variant="caption" sx={{ color: colors?.textSecondary || '#666', fontStyle: 'italic', px: 0.5 }}>
              {searchTerm ? 'No matching components' : 'No reusable components'}
            </Typography>
          ) : (
            Object.entries(groupedComponents).map(([type, group]) => {
              const TypeIcon = COMPONENT_TYPE_ICONS[type] || ExtensionIcon;
              return (
                <Box key={type} sx={{ mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: '#22c55e', fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.5, px: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <TypeIcon sx={{ fontSize: 12 }} />
                    {COMPONENT_TYPE_LABELS[type] || type}
                  </Typography>
                  {group.map((comp) => (
                    <PaletteChip
                      key={comp.id || comp.componentCode}
                      id={`palette-component-${comp.componentCode}`}
                      icon={<ExtensionIcon />}
                      label={comp.displayName || comp.componentCode}
                      dragData={{
                        type: 'component-ref',
                        source: 'palette',
                        componentCode: comp.componentCode,
                        componentType: comp.componentType,
                        displayName: comp.displayName || comp.componentCode,
                        description: comp.description,
                      }}
                      colors={colors}
                    />
                  ))}
                </Box>
              );
            })
          )}
        </PaletteSection>

        <Divider sx={{ borderColor: colors?.border || '#333', mx: 1.5 }} />

        {/* Layout Components */}
        <PaletteSection title="Layout" colors={colors}>
          <PaletteChip
            id="palette-tab"
            icon={<TabIcon />}
            label="Tab (View)"
            dragData={{ type: 'layout', layoutType: 'tab' }}
            colors={colors}
          />
          <PaletteChip
            id="palette-section"
            icon={<HorizontalRuleIcon />}
            label="Section"
            dragData={{ type: 'layout', layoutType: 'section' }}
            colors={colors}
          />
          <PaletteChip
            id="palette-columns"
            icon={<ViewColumnIcon />}
            label="Columns"
            dragData={{ type: 'layout', layoutType: 'columns' }}
            colors={colors}
          />
        </PaletteSection>

        <Divider sx={{ borderColor: colors?.border || '#333', mx: 1.5 }} />

        {/* Panel Components */}
        <PaletteSection title="Panels" colors={colors}>
          <PaletteChip
            id="palette-grid"
            icon={<TableChartIcon />}
            label="Grid"
            dragData={{ type: 'panel', panelType: 'grid', role: 'master' }}
            colors={colors}
          />
          <PaletteChip
            id="palette-detail-grid"
            icon={<ListAltIcon />}
            label="Detail Grid"
            dragData={{ type: 'panel', panelType: 'detail-grid', role: 'detail' }}
            colors={colors}
          />
          <PaletteChip
            id="palette-form"
            icon={<EditNoteIcon />}
            label="Form"
            dragData={{ type: 'panel', panelType: 'form', role: 'master' }}
            colors={colors}
          />
          <PaletteChip
            id="palette-kpi"
            icon={<SpeedIcon />}
            label="KPI Card"
            dragData={{ type: 'panel', panelType: 'kpi' }}
            colors={colors}
          />
          <PaletteChip
            id="palette-chart"
            icon={<BarChartIcon />}
            label="Chart"
            dragData={{ type: 'panel', panelType: 'chart' }}
            colors={colors}
          />
        </PaletteSection>

        <Divider sx={{ borderColor: colors?.border || '#333', mx: 1.5 }} />

        {/* Field Types — user drags a type, then configures entity/field in properties */}
        <PaletteSection title="Field Types" defaultOpen={true} colors={colors}>
          {FIELD_TYPES.map((ft) => (
            <PaletteChip
              key={ft.fieldType}
              id={`palette-fieldtype-${ft.fieldType}`}
              icon={
                <Typography
                  component="span"
                  sx={{ fontSize: 11, fontWeight: 700, width: 20, textAlign: 'center' }}
                >
                  {ft.icon}
                </Typography>
              }
              label={ft.label}
              dragData={{
                type: 'field-type',
                source: 'palette',
                fieldType: ft.fieldType,
                displayLabel: ft.label,
              }}
              colors={colors}
            />
          ))}
        </PaletteSection>

        <Divider sx={{ borderColor: colors?.border || '#333', mx: 1.5 }} />

        {/* Action Types */}
        <PaletteSection title="Actions" defaultOpen={false} colors={colors}>
          {ACTION_TYPES.map((at) => (
            <PaletteChip
              key={at.actionType}
              id={`palette-action-${at.actionType}`}
              icon={
                <Typography component="span" sx={{ fontSize: 13 }}>{at.icon}</Typography>
              }
              label={at.label}
              dragData={{
                type: 'action',
                source: 'palette',
                actionType: at.actionType,
                label: at.label,
              }}
              colors={colors}
            />
          ))}
        </PaletteSection>
      </Box>
    </Box>
  );
}
